import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { fieldErrorsFrom422, formatApiErrorDetail, isValidUrl } from "@/lib/api";
import { FormField, adminInput } from "./shared";

const BLANK = { name: "", url: "", description: "", image_url: "", visible: true };

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Name is required";
  if (!f.url.trim()) e.url = "URL is required";
  else if (!isValidUrl(f.url.trim())) e.url = "Please enter a valid URL (https://...)";
  if (f.image_url.trim() && !isValidUrl(f.image_url.trim())) e.image_url = "Please enter a valid image URL";
  return e;
};

export const PortfolioDialog = ({ open, item, onClose, onSave }) => {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (open) { setForm(item ? { ...BLANK, ...item } : BLANK); setErrors({}); setServerError(""); }
  }, [open, item]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { name, url, description, image_url, visible } = form;
      await onSave({ name, url, description, image_url, visible });
    } catch (err) {
      const fe = fieldErrorsFrom422(err.response?.data?.detail);
      if (Object.keys(fe).length) setErrors(fe);
      else setServerError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="portfolio-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#0D111A]">
        <DialogHeader><DialogTitle className="text-white">{item ? "Edit website" : "Add website"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <FormField id="portfolio-name" label="Name" error={errors.name}>
            <input id="portfolio-name" data-testid="portfolio-input-name" value={form.name} onChange={set("name")} className={adminInput(errors.name)} />
          </FormField>
          <FormField id="portfolio-url" label="Site URL" error={errors.url}>
            <input id="portfolio-url" data-testid="portfolio-input-url" value={form.url} onChange={set("url")} placeholder="https://" className={adminInput(errors.url)} />
          </FormField>
          <FormField id="portfolio-description" label="Description">
            <textarea id="portfolio-description" rows={3} data-testid="portfolio-input-description" value={form.description} onChange={set("description")} className={adminInput(false)} />
          </FormField>
          <FormField id="portfolio-image" label="Image URL (leave empty to auto-generate an AI photo)" error={errors.image_url}>
            <input id="portfolio-image" data-testid="portfolio-input-image-url" value={form.image_url} onChange={set("image_url")} placeholder="https://" className={adminInput(errors.image_url)} />
          </FormField>
          <label className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-3 text-sm text-slate-300">
            Visible on public Portfolio
            <Switch checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="portfolio-input-visible" />
          </label>
          {serverError && <p data-testid="portfolio-server-error" className="text-sm text-red-400">{serverError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} data-testid="portfolio-cancel-button" className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="portfolio-save-button" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
